import { API_BASE } from './base'
import type { NavPoint, BacktestDeatil ,StartBacktestParams ,BacktestTradesDetail ,CompareBacktestResponse, BacktestReasonDetail, AISignal, AISignalsListResponse, BenchmarkNavResponse, StockPoolNavResponse, HoldingsSnapshot } from '@/types'

function formatApiDetail(detail: unknown, fallback: string): string {
    if (typeof detail === 'string' && detail.trim()) {
        return detail
    }
    if (Array.isArray(detail)) {
        const parts = detail
            .map((item) => {
                if (typeof item === 'string') return item
                if (item && typeof item === 'object' && 'msg' in item) {
                    return String((item as { msg?: unknown }).msg || '')
                }
                return ''
            })
            .filter(Boolean)
        if (parts.length) return parts.join('；')
    }
    return fallback
}

//回测
export async function startBacktest(params: StartBacktestParams): Promise<{ run_id: string }> {
    const res = await fetch(`${API_BASE}/backtest/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(params),
    })
    const body = (await res.json().catch(() => ({}))) as {
        run_id?: string
        detail?: unknown
    }
    if (!res.ok) {
        throw new Error(formatApiDetail(body.detail, `启动回测失败（HTTP ${res.status}）`))
    }
    if (!body.run_id) {
        throw new Error('启动回测失败：未返回 run_id')
    }
    return { run_id: body.run_id }
}

/** 订阅 SSE，返回 cleanup 函数 */
export function subscribeSSE(
    runId: string,
    onData: (d: NavPoint) => void,
    onDone: () => void,
): () => void {
    const es = new EventSource(`${API_BASE}/backtest/${runId}/stream`)
    es.onmessage = (e) => {
        if (e.data === '[DONE]') {
            onDone()
            es.close()
        } else {
            onData(JSON.parse(e.data) as NavPoint)
        }
    }
    es.onerror = () => {
        onDone()
        es.close()
    }
    return () => es.close()
}

//ai策略
export async function saveStyle(content: string, styleName = 'conservative'): Promise<void> {
    await fetch(`${API_BASE}/config/investment-style`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content, style_name: styleName }),
    })
}

//回测列表 -- 最近回测
export async function getRecentBacktests(): Promise<
{before_this_week: BacktestDeatil[], this_week: BacktestDeatil[],today: BacktestDeatil[]}> {
    const res = await fetch(`${API_BASE}/backtests/recent`)
    return res.json()
}

//回测列表 -- 历史回测
export async function getBacktestsHistory(data:{start_date?: string, end_date?: string}): Promise<BacktestDeatil[]> {
    const res = await fetch(`${API_BASE}/backtests/history?${new URLSearchParams(data).toString()}`)
    return res.json()
}

//回测列表 -- 已删除回测
export async function getBacktestsDeleted(): Promise<BacktestDeatil[]> {
    const res = await fetch(`${API_BASE}/backtests/deleted`)
    return res.json()
}

//回测详情 -- 回测详情（主数据）
export async function getBacktestDetail(run_id: string): Promise<BacktestDeatil> {
    const res = await fetch(`${API_BASE}/backtests/run/${run_id}`)
    return res.json()
}

//回测详情 -- 回测详情（nav数据）   
export async function getBacktestNavDetail(id: number): Promise<{dates: string[], items: NavPoint[]}> {
    const res = await fetch(`${API_BASE}/backtests/${id }/nav`)
    return res.json()
}

//回测详情 -- 绑定股票池等权涨跌幅（前复权日K）
export async function getBacktestStockPoolNav(id: number): Promise<StockPoolNavResponse> {
    const res = await fetch(`${API_BASE}/backtests/${id}/stock-pool-nav`)
    const body = (await res.json().catch(() => ({}))) as StockPoolNavResponse & { detail?: unknown }
    if (!res.ok) {
        throw new Error(formatApiDetail(body.detail, `加载股票池涨跌幅失败（HTTP ${res.status}）`))
    }
    return body
}

//回测详情 -- 某一日持仓
export async function getBacktestHoldings(id: number, asOf: string): Promise<HoldingsSnapshot> {
    const params = new URLSearchParams({ as_of: asOf })
    const res = await fetch(`${API_BASE}/backtests/${id}/holdings?${params}`)
    const body = (await res.json().catch(() => ({}))) as HoldingsSnapshot & { detail?: unknown }
    if (!res.ok) {
        throw new Error(formatApiDetail(body.detail, `查询持仓失败（HTTP ${res.status}）`))
    }
    return body
}

//回测详情 -- 基准指数对齐净值曲线
export async function getBacktestBenchmarkNav(id: number, benchmark?: string): Promise<BenchmarkNavResponse> {
    const params = new URLSearchParams()
    if (benchmark) {
        params.set('benchmark', benchmark)
    }
    const query = params.toString()
    const res = await fetch(`${API_BASE}/backtests/${id}/benchmark-nav${query ? `?${query}` : ''}`)
    return res.json()
}

//回测详情 -- 回测详情（交易数据）   
export async function getBacktestTradesDetail(id: number) : Promise<{ items: BacktestTradesDetail[]}> {
    const res = await fetch(`${API_BASE}/backtests/${id}/trades`)
    return res.json()
}

//回测详情 -- 回测详情（持仓归因）   
export async function getBacktestReasonDetail(id: number): Promise<{
    items: BacktestReasonDetail[]
    open_position_count?: number
    open_unrealized_pnl?: number
    total_return_pct?: number | null
}> {
    const res = await fetch(`${API_BASE}/backtests/${id }/holding-attribution`)
    return res.json()
}

//回测详情 -- 净值回撤曲线
export async function getBacktestNavDrawdownDetail(run_id: string) {
    const res = await fetch(`${API_BASE}/backtests/run/${run_id}/nav-drawdown`)
    return res.json()
}

//回测删除
export async function deleteBacktest(id: number): Promise<void> {
    await fetch(`${API_BASE}/backtests/${id}`, {
        method: 'DELETE',
    })
}

//回测归档
export async function archiveBacktest(id: number, record_status: number): Promise<void> {
    await fetch(`${API_BASE}/backtests/${id}/archive`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ record_status }),
    })
}

//多回测对比
export async function getBacktestCompare(ids: number[]) :Promise<CompareBacktestResponse> {
    const res = await fetch(`${API_BASE}/backtests/compare`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids }),
    })
    return res.json()
}

/**
 * AI信号
 * @param data.backtest_id 回测ID
 * @param data.signal_type 信号类型: REBALANCE,STOP_LOSS,TAKE_PROFIT,PE_CEILING,REPORT_PUBLISHED,RESEARCH_UPDATE
 * 基本面：REBALANCE,REPORT_PUBLISHED
 * K线：STOP_LOSS,TAKE_PROFIT,PE_CEILING
 * 研报：RESEARCH_UPDATE
 * **/
export async function getAISignals(data: {
    backtest_id: number
    signal_type: string
    limit?: number
    offset?: number
}): Promise<AISignalsListResponse> {
    const params = new URLSearchParams({ signal_type: data.signal_type })
    if (data.limit != null) params.set('limit', String(data.limit))
    if (data.offset != null) params.set('offset', String(data.offset))
    const res = await fetch(`${API_BASE}/backtests/${data.backtest_id}/signals?${params}`)
    return res.json()
}

export async function getAISignalDetail(
    backtest_id: number,
    signal_id: number,
): Promise<{ backtest_id: number; item: AISignal }> {
    const res = await fetch(`${API_BASE}/backtests/${backtest_id}/signals/${signal_id}`)
    return res.json()
}